/**
 * ChannelSetupAssistant — geführte Hilfe, wenn ein Kanal noch nicht verbindbar ist.
 *
 * Öffnet sich statt einer Fehlermeldung und führt Schritt für Schritt:
 *  - sagt einfach, was noch fehlt (Zugangsdaten vs. Weiterleitungs-Adresse)
 *  - zeigt die exakte Weiterleitungs-Adresse + Copy-Button
 *  - erklärt, wo man sie im Portal einträgt (+ Direktlink)
 *  - „Erneut prüfen" → bei Bereitschaft „Jetzt verbinden"
 *
 * Kein Fake-Connect: „Jetzt verbinden" erscheint nur, wenn config-check ready=true meldet.
 */
import { useState } from "react";
import { X, Copy, CopyCheck, ExternalLink, CheckCircle, AlertTriangle, Loader2, RefreshCw, Plug, Sparkles } from "lucide-react";
import { PORTALS, CHANNEL_INTRO, categorizeMissing } from "./channelHelp";
import { fetchSetupHelp } from "../../services/socialOAuth";

function CopyValue({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* manuell markieren */ }
  };
  return (
    <div className="flex items-center gap-2 mt-1">
      <code className="flex-1 text-[11px] bg-stone-50 border border-stone-200 rounded px-2 py-1.5 text-stone-600 break-all">{value}</code>
      <button onClick={copy} className="flex items-center gap-1 text-xs text-fox-fire hover:text-orange-700 flex-shrink-0">
        {copied ? <CopyCheck size={13} /> : <Copy size={13} />}{copied ? "Kopiert" : "Kopieren"}
      </button>
    </div>
  );
}

function StatusPill({ ready }) {
  return ready ? (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600"><CheckCircle size={13} /> Bereit</span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600"><AlertTriangle size={13} /> Noch nicht fertig</span>
  );
}

export default function ChannelSetupAssistant({ open, channelId, channelLabel, info, rechecking, onRecheck, onConnectNow, onClose, setupHelpEnabled }) {
  const [helpText, setHelpText] = useState(null);
  const [helpLoading, setHelpLoading] = useState(false);
  const [question, setQuestion] = useState("");

  if (!open) return null;

  const portal = PORTALS[channelId] || {};
  const ready = !!info?.ready;
  const redirectUri = info?.redirect_uri;
  const { needsCredentials, needsRedirect } = categorizeMissing(info?.missing_env || []);

  const askHelp = async () => {
    setHelpLoading(true);
    setHelpText(null);
    const res = await fetchSetupHelp({
      platform: channelId,
      ready,
      missing_env: info?.missing_env || [],
      redirect_uri: redirectUri || null,
      portal_name: portal.portal || null,
      portal_url: portal.url || null,
      user_question: question.trim() || null,
    });
    setHelpLoading(false);
    setHelpText(
      res?.help_text ||
      "Die KI-Hilfe ist gerade nicht verfügbar. Du kannst die Schritte oben trotzdem ganz normal weitermachen."
    );
  };
  // Zugangsdaten gelten als erledigt, wenn config-check „configured" meldet
  const credsDone = info?.configured === true && !needsCredentials;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-stone-100">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0"><Plug size={16} className="text-orange-500" /></div>
            <h3 className="font-semibold text-stone-800 truncate">{channelLabel} einrichten</h3>
            <StatusPill ready={ready} />
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600 flex-shrink-0"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-stone-500">{CHANNEL_INTRO[channelId] || "Folge den Schritten, um diesen Kanal zu verbinden."}</p>

          {ready ? (
            <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle size={15} className="text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-700">Alles bereit! Du kannst diesen Kanal jetzt verbinden.</p>
            </div>
          ) : (
            <>
              {/* Was noch fehlt — einfache Checkliste */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Das fehlt noch</p>

                {/* 1. Zugangsdaten */}
                <div className="flex items-start gap-2">
                  {credsDone ? <CheckCircle size={15} className="text-green-500 mt-0.5 flex-shrink-0" /> : <AlertTriangle size={15} className="text-amber-500 mt-0.5 flex-shrink-0" />}
                  <div>
                    <p className="text-sm text-stone-700">Zugangsdaten (App-ID &amp; Schlüssel)</p>
                    {!credsDone && (
                      <p className="text-xs text-stone-500">Werden einmalig in der App-Konfiguration hinterlegt (durch den Administrator).</p>
                    )}
                  </div>
                </div>

                {/* 2. Weiterleitungs-Adresse im Portal */}
                {redirectUri && (
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={15} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-stone-700">Weiterleitungs-Adresse im Portal eintragen</p>
                      <p className="text-xs text-stone-500">{portal.portal ? <>In <strong>{portal.portal}</strong>: {portal.where}</> : "Im jeweiligen Entwickler-Portal hinterlegen."}</p>
                      <CopyValue value={redirectUri} />
                      <p className="text-[11px] text-stone-400 mt-1">Wichtig: <strong>exakt</strong> so eintragen (mit https, ohne Schrägstrich am Ende).</p>
                      {portal.url && (
                        <a href={portal.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-fox-fire hover:text-orange-700">
                          Portal öffnen <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-start gap-2 p-3 bg-stone-50 border border-stone-200 rounded-lg">
                <RefreshCw size={13} className="text-stone-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-stone-500">Fertig eingetragen? Tippe auf <strong>„Erneut prüfen"</strong>. Sobald alles passt, erscheint „Jetzt verbinden".</p>
              </div>
            </>
          )}

          {/* Optionale KI-Hilfe (nur wenn serverseitig aktiviert) */}
          {setupHelpEnabled && (
            <div className="border-t border-stone-100 pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} className="text-violet-500" />
                <p className="text-sm font-semibold text-stone-700">In einfachen Worten erklärt</p>
              </div>
              {helpText && (
                <div className="text-sm text-stone-600 bg-violet-50 border border-violet-100 rounded-lg p-3 whitespace-pre-line">{helpText}</div>
              )}
              {helpLoading && <p className="text-xs text-stone-400 mt-2">KI-Hilfe wird geladen…</p>}
              <div className="flex gap-2 mt-2">
                <input
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") askHelp(); }}
                  placeholder="Was ist dir noch unklar? (optional)"
                  className="flex-1 px-3 py-2 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                />
                <button onClick={askHelp} disabled={helpLoading}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-violet-500 hover:bg-violet-600 text-white rounded-lg text-sm font-medium disabled:opacity-40 flex-shrink-0">
                  {helpLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {helpLoading ? "Lädt…" : "Erklären lassen"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Aktionen */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-stone-100">
          <button onClick={onClose} className="text-sm text-stone-500 hover:text-stone-700">Schließen</button>
          {!ready && (
            <button onClick={onRecheck} disabled={rechecking}
              className="inline-flex items-center gap-2 px-4 py-2 border border-stone-200 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-50 disabled:opacity-40">
              {rechecking ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
              Erneut prüfen
            </button>
          )}
          {ready && (
            <button onClick={onConnectNow}
              className="inline-flex items-center gap-2 px-4 py-2 bg-fox-fire hover:bg-orange-600 text-white rounded-lg text-sm font-medium">
              <Plug size={15} /> Jetzt verbinden
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
